import { isNil, flatten } from 'lodash';
import { i18n } from '@src/i18n-setup';
import getField, { FieldsEnum, getFieldString, getFieldCurrency, getFieldPercent } from '@utils/fields/common';
import addressDescription from '@utils/fields/address';
import getDate from '@utils/date/get-date';
import LoanTypeEnum from '@enums/loan-type-enum';
import { IncomeEnum } from '@enums/resource-enum';

/**
 * Helper functions for enquiry details view.
 */

/**
 * Helper function that returns enquiry details fields for `post detail tab`.
 * @public
 * @param {Object} enquiry - The enquiry to represent.
 * @return {Object} - The entries for the enquiry.
 */
export function getPostDetailsData(enquiry) {
  if (!enquiry) {
    return { specs: [] };
  }

  const homeLoan = enquiry.target_resources[0];

  return [
    {
      title: i18n.t('enquiry.field.loan_type'),
      value: getFieldString(`enquiry.field.loan_type_${homeLoan.data.loan_type.replace(' ', '_')}`, homeLoan.data.loan_type),
    },
    {
      title: i18n.t('enquiry.field.loan_purpose'),
      value: getFieldString(`enquiry.field.loan_purpose_${homeLoan.data.loan_purpose}`, homeLoan.data.loan_purpose),
    },
    {
      title: i18n.t('enquiry.field.loan_amount'),
      value: getFieldCurrency(homeLoan.data.value),
    },
    {
      title: i18n.t('enquiry.field.property_address'),
      value: addressDescription(homeLoan.data),
    },
    {
      title: i18n.t('enquiry.field.property_market_value'),
      value: getFieldCurrency(homeLoan.data.property_market_value),
    },
    {
      title: i18n.t(`enquiry.field.current_lender.${homeLoan.data.loan_type.replace(' ', '_')}`),
      value: getField(homeLoan.organisation.name),
    },
    ...refinanceFields(homeLoan),
    {
      title: i18n.t('enquiry.field.preferred_rate_type'),
      value: getFieldString(`enquiry.preference_option.${homeLoan.data.preferences.rate_type}`, homeLoan.data.preferences.rate_type),
    },
    {
      title: i18n.t('enquiry.field.preferred_repayment_type'),
      value: getFieldString(`enquiry.preference_option.${homeLoan.data.preferences.repayment_type}`, homeLoan.data.preferences.repayment_type),
    },
    {
      title: i18n.t('enquiry.preference.offset_account'),
      value: getFieldString(`enquiry.preference_option.${homeLoan.data.preferences.offset_account}`, homeLoan.data.preferences.offset_account),
    },
    {
      title: i18n.t('enquiry.field.additional_security_value'),
      value: getFieldCurrency(homeLoan.data.security_property_value),
    },
  ];
}

/**
 * Helper function that returns enquiry details fields for `savings and incomes tab`.
 * @public
 */
export function getApplicantsData(enquiry) {
  if (!enquiry) {
    return [];
  }

  // @TODO: Fix this
  return [...(enquiry.users || [])]
    .sort((a, b) => a.number - b.number)
    .map((applicant) => {
      const resource = findResource(
        enquiry.user_resources,
        applicant.uuid,
        IncomeEnum.EMPLOYMENT_STATUS,
      );
      const resourceData = resource ? resource.data : {};

      return {
        title: i18n.t('enquiry.field.applicant_number', { number: applicant.number }),
        data: [
          {
            title: i18n.t('enquiry.field.gross_annual_income'),
            value: getFieldCurrency(resourceData.salary),
          },
          {
            title: i18n.t('enquiry.field.occupation'),
            value: getField(resourceData.job_title),
          },
          {
            title: i18n.t('enquiry.field.industry'),
            value: getField(resourceData.company_name),
          },
          {
            title: i18n.t('enquiry.field.employment_status'),
            value: getFieldString(`enquiry.employment_status.${resourceData.employment_status}`, resourceData.employment_status),
          },
          {
            title: i18n.t('enquiry.field.age'),
            value: getAgeField(applicant.date_of_birth),
          },
        ],
      };
    });
}

/**
 * If the home loan is of type refinance, returns the related information.
 *
 * @param {Object} homeLoan - The home loan.
 * @return {Array} - A collection of entries.
 */
function refinanceFields(homeLoan) {
  if (homeLoan.data.loan_type === LoanTypeEnum.REFINANCE) {
    return [
      { title: i18n.t('enquiry.field.current_interest_rate'), value: getFieldPercent(homeLoan.data.interest_rate) },
    ];
  }

  return [];
}

function getAgeField(date) {
  if (isNil(date)) {
    return FieldsEnum.EMPTY;
  }

  const birthDate = getDate(date);
  const birthYear = birthDate.getFullYear();
  const now = new Date();
  let yearsOld = (now.getFullYear() - birthYear);

  if (now.getMonth() < birthDate.getMonth() ||
      (now.getMonth() === birthDate.getMonth() && now.getDate() < birthDate.getDate())) {
    yearsOld -= 1;
  }

  return `${yearsOld} (${birthYear})`;
}


/**
 * Find a resource based on the given type and applicant uuid.
 *
 * @param {Array} resources - All the resources.
 * @param {String} applicantUuid - The uuid of the applicant.
 * @param {String} resourceType - The type of resource.
 * @return {Object}
 */
function findResource(resources, applicantUuid, resourceType) {
  return flatten(resources)
    .find((resource) => {
      const userReference = resource.user_references[0].uuid;

      return userReference === applicantUuid && resource.type === resourceType;
    });
}
